import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
} from '@tanstack/table-core';

/**
 * Shared `prism-table` wrapper replacing the vendored `react-bootstrap-table2`.
 *
 * Cells render through `createPortal` into stable host nodes rather than
 * `ReactDOM.render` into detached divs. That choice is load-bearing: portalled cells
 * stay in the main React tree, so they keep context (toasts, auth, router) and unmount
 * properly. Verified in `client/design-lab/prism-table-cells.html`, which also confirms
 * focus and blur work inside `prism-table`'s shadow root.
 */

export type DataGridAlign = 'left' | 'center' | 'right';

export interface DataGridEditor<T> {
  /** 'select' renders a dropdown; anything else renders a text/number input. */
  type?: 'text' | 'number' | 'select';
  options?: Array<{ label: string; value: string }>;
  /** Return `true` to accept, or a message to reject and revert. */
  validate?: (value: string, row: T) => true | { valid: false; message: string };
  onSave: (row: T, value: string) => void | Promise<void>;
}

export interface DataGridColumn<T> {
  id: string;
  header: string;
  /** Value used for sorting, filtering and the default text rendering. */
  accessor?: (row: T) => unknown;
  /** React content for the cell. Rendered via portal. */
  cell?: (row: T) => React.ReactNode;
  sortable?: boolean;
  filter?: 'text' | 'select' | 'range';
  /** Options for `filter: 'select'`. */
  selectOptions?: Array<{ label: string; value: string }>;
  filterPlaceholder?: string;
  /** Value used for filtering when it differs from the sort/display value. */
  filterValue?: (row: T) => string;
  width?: string;
  align?: DataGridAlign;
  editable?: DataGridEditor<T>;
}

export interface PrismDataGridProps<T> {
  data: T[];
  columns: Array<DataGridColumn<T>>;
  /** Field holding each row's stable unique id. */
  keyField: keyof T & string;
  caption: string;
  /** Enables prism-table's built-in pagination control. */
  pageSize?: number;
  /**
   * Renders a "Rows per page" selector above the table. Opt-in, because most grids in the
   * app are short enough that the control is noise. Values must include `pageSize`.
   *
   * The selection is applied through `getTable()` → `setPageSize()` rather than by changing
   * the `pageSize` prop, because `pageSize` feeds `options.initialState` and reassigning
   * `options` resets row selection and column filters.
   */
  pageSizeOptions?: number[];
  defaultSort?: Array<{ id: string; desc?: boolean }>;
  emptyMessage?: string;
  className?: string;
  /** Surfaces validation failures. Callers pass their toast helper. */
  onValidationError?: (message: string) => void;
  /**
   * Per-row inline styles. prism-table renders rows in its shadow root and exposes only
   * a blanket `::part(tr)`, so conditional row styling cannot be done from a stylesheet.
   * Inline styles cross the shadow boundary, at the cost of not supporting `:hover`.
   */
  rowStyle?: (row: T) => Record<string, string> | undefined;
  /**
   * Receives the rows surviving the current filters, in sorted order. Used by callers
   * that export or count "the current view".
   */
  onFilteredRowsChange?: (rows: T[]) => void;

  /**
   * Renders a checkbox selection column. `prism-table` injects the column UI itself
   * when a column with `id: '__select__'` is present and `enableRowSelection` is set.
   */
  enableSelection?: boolean;

  /** Fired with the full set of currently-selected rows whenever selection changes. */
  onSelectionChange?: (rows: T[]) => void;
}

interface HostEntry {
  node: HTMLElement;
  rowId: string;
  colId: string;
}

// Cell content is portalled into `prism-table`'s shadow root, which page stylesheets cannot
// reach. Without explicit styles the browser's UA defaults apply: the edit trigger renders as a
// grey beveled button and the editors as white fields with black text — a white island on the
// dark theme. These must therefore be inline, and must resolve theme tokens rather than literals.
const EDIT_TRIGGER_STYLE: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'inherit',
  textAlign: 'inherit',
  cursor: 'pointer',
  width: '100%',
};

const EDITOR_STYLE: React.CSSProperties = {
  font: 'inherit',
  color: 'var(--color-text)',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: '4px',
  padding: '2px 4px',
  width: '100%',
  boxSizing: 'border-box',
};

// The grid has no companion stylesheet — every style in this file is inline, so the page
// size control follows suit rather than introducing one.
const PAGE_SIZE_ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '0.5rem',
  marginBottom: '0.5rem',
  fontSize: '0.875rem',
  color: 'var(--color-text-faded)',
};

const PAGE_SIZE_SELECT_STYLE: React.CSSProperties = {
  font: 'inherit',
  color: 'var(--color-text)',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: '4px',
  padding: '2px 6px',
};

let gridInstanceCount = 0;

// Columns may omit an accessor and fall back to the field named by the column id.
// T is only constrained to `object` so consumers can pass plain interfaces, which have
// no implicit index signature.
function readField(row: object, field: string): unknown {
  return (row as Record<string, unknown>)[field];
}

function PrismDataGrid<T extends object>({
  data,
  columns,
  keyField,
  caption,
  pageSize,
  pageSizeOptions,
  defaultSort,
  emptyMessage = 'No records found.',
  className,
  onValidationError,
  onFilteredRowsChange,
  rowStyle,
  enableSelection,
  onSelectionChange,
}: PrismDataGridProps<T>) {
  const tableRef = useRef<HTMLElement & {
    data?: unknown;
    columns?: unknown;
    options?: unknown;
    getTable?: () => Promise<{
      getFilteredRowModel: () => { rows: Array<{ original: T }> };
      setPageSize?: (size: number) => void;
    }>;
  }>(null);

  // A module counter captured once per instance is stable across renders and unique within
  // the document, which is all the label/select pairing needs.
  const gridIdRef = useRef<string | undefined>(undefined);
  if (!gridIdRef.current) {
    gridInstanceCount += 1;
    gridIdRef.current = `prism-grid-${gridInstanceCount}`;
  }
  const gridId = gridIdRef.current;
  const [activePageSize, setActivePageSize] = useState(pageSize ?? 0);

  // Host nodes are keyed by `rowId:colId` and reused across renders. Replacing a host
  // would detach the portal's subtree and lose the cell's local state mid-edit.
  const hostsRef = useRef<Map<string, HostEntry>>(new Map());
  const lastSelectionRef = useRef<T[]>([]);
  const lastFilteredRef = useRef<T[] | null>(null);
  const [, forceRender] = useState(0);
  const flushQueued = useRef(false);

  // prism-table calls cell renderers during its own render pass, so new hosts cannot
  // trigger a React update synchronously. Batch them into one deferred re-render.
  const queueFlush = useCallback(() => {
    if (flushQueued.current) return;
    flushQueued.current = true;
    queueMicrotask(() => {
      flushQueued.current = false;
      forceRender((n) => n + 1);
    });
  }, []);

  const [editing, setEditing] = useState<{ rowId: string; colId: string } | null>(null);

  const rowIdOf = useCallback(
    (row: T) => String(row[keyField]),
    [keyField]
  );

  // Consumers routinely build their column array inline (`columns={this.getColumns()}`), so
  // the array identity changes on every render. That is harmless for rendering but fatal for
  // state: reassigning `el.options` makes prism-table call setOptions on its TanStack
  // instance, which resets rowSelection and columnFilters. The symptom is that selecting rows
  // or setting a filter appears to do nothing, because the consumer's own re-render wipes the
  // state the interaction just produced.
  //
  // So the memo keys off a structural signature rather than array identity, and the live
  // column array is read through a ref so callbacks never close over a stale copy.
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  const columnsKey = useMemo(
    () =>
      JSON.stringify(
        columns.map((col) => [
          col.id,
          typeof col.header === 'string' ? col.header : null,
          !!col.sortable,
          col.filter ?? null,
          col.selectOptions ?? null,
          col.filterPlaceholder ?? null,
          col.align ?? null,
          !!col.editable,
          !!col.cell,
          !!col.accessor,
          !!col.filterValue,
        ])
      ),
    [columns]
  );

  const tanstackColumns = useMemo(() => {
    const mapped = columnsRef.current.map((col, index) => ({
      id: col.id,
      // Resolved through the ref at call time, so a re-rendered consumer's fresh closures
      // (which capture current state) are used even though this memo did not recompute.
      accessorFn: (row: T) => {
        const live = columnsRef.current[index] ?? col;
        return live.accessor ? live.accessor(row) : readField(row, live.id);
      },
      header: col.header,
      enableSorting: !!col.sortable,
      enableColumnFilter: !!col.filter,
      // Conditional *spread*, never `filterFn: undefined`. TanStack merges each column def
      // over a default that supplies `filterFn: 'auto'`, and an explicitly-present key whose
      // value is undefined overrides that default rather than falling back to it. The lookup
      // then fails with "Could not find a valid 'column.filterFn'" and the column silently
      // stops filtering — the control still records the value, so the filter reads as applied
      // while every row survives. Measured in design-lab/applications-grid-probe.html.
      ...(col.filterValue
        ? {
            filterFn: (
              tanstackRow: { original: T },
              _colId: string,
              filterValue: string
            ) => {
              const live = columnsRef.current[index] ?? col;
              return String(live.filterValue?.(tanstackRow.original) ?? '')
                .toLowerCase()
                .includes(String(filterValue).toLowerCase());
            },
          }
        : {}),
      ...(col.filter
        ? {
            meta: {
              filterVariant: col.filter,
              selectOptions: col.selectOptions,
              filterPlaceholder: col.filterPlaceholder,
            },
          }
        : {}),
      cell: (info: { row: { original: T } }) => {
        const live = columnsRef.current[index] ?? col;
        const row = info.row.original;
        const rowId = rowIdOf(row);
        const key = `${rowId}:${live.id}`;

        // Plain value columns with no React content and no editor need no portal.
        if (!live.cell && !live.editable) {
          const value = live.accessor ? live.accessor(row) : readField(row, live.id);
          return value == null ? '' : String(value);
        }

        let entry = hostsRef.current.get(key);
        if (!entry) {
          const node = document.createElement('span');
          node.className = 'prism-grid-cell';
          if (live.align) node.style.textAlign = live.align;
          entry = { node, rowId, colId: live.id };
          hostsRef.current.set(key, entry);
          queueFlush();
        }
        return entry.node;
      },
    }));

    // prism-table injects the checkbox UI for this reserved id; the placeholder only
    // has to exist so the column takes a position in the header/body row.
    return enableSelection
      ? [{ id: '__select__', header: '', enableSorting: false, enableColumnFilter: false }, ...mapped]
      : mapped;
    // columnsKey, not columns: see the note above the signature.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnsKey, rowIdOf, queueFlush, enableSelection]);

  // Drop hosts for rows that no longer exist, otherwise the map grows without bound.
  useEffect(() => {
    const live = new Set(data.map((row) => rowIdOf(row)));
    let removed = false;
    hostsRef.current.forEach((entry, key) => {
      if (!live.has(entry.rowId)) {
        hostsRef.current.delete(key);
        removed = true;
      }
    });
    if (removed) queueFlush();
  }, [data, rowIdOf, queueFlush]);

  // Same identity hazard as `columns`: an inline `defaultSort={[{ id: 'x' }]}` would otherwise
  // reassign options on every render and reset selection and filters.
  const defaultSortKey = JSON.stringify(defaultSort ?? null);

  // Three separate effects, in declaration order, because the assignment order matters on a
  // cold load and the reassignment *triggers* differ. prism-table's `data` watcher calls
  // setOptions on its TanStack instance, which only exists once the element has been
  // configured — assigning `data` first throws ("Cannot read properties of undefined
  // (reading 'setOptions')") and the throw aborts the rest of the effect.
  //
  // Keeping them apart is what preserves interaction state: `options` carries initialState,
  // so reassigning it resets row selection and column filters. It must therefore change only
  // when the configuration genuinely changes, never because `data` or a re-rendered column
  // array arrived.
  useLayoutEffect(() => {
    const el = tableRef.current;
    if (!el) return;
    el.options = {
      // Without an explicit row id TanStack identifies rows by index, so deleting a row
      // shifts every row's identity and prism-table reuses the wrong cell nodes — the
      // surviving rows render stale or blank. Measured in design-lab/prism-data-grid.html.
      getRowId: (row: T) => rowIdOf(row),
      ...(enableSelection ? { enableRowSelection: true } : {}),
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      ...(pageSize
        ? {
            getPaginationRowModel: getPaginationRowModel(),
          }
        : {}),
      initialState: {
        ...(pageSize ? { pagination: { pageSize } } : {}),
        ...(defaultSort
          ? { sorting: defaultSort.map((s) => ({ id: s.id, desc: !!s.desc })) }
          : {}),
      },
    };
  }, [pageSize, defaultSortKey, rowIdOf, enableSelection]); // eslint-disable-line react-hooks/exhaustive-deps

  // Page size is applied imperatively, never through `options`. `options` carries
  // initialState, so reassigning it to change the page size would clear row selection and
  // every column filter — the exact state a user is most likely to have set before deciding
  // the page is too short. Runs after the options effect so the TanStack instance exists.
  useEffect(() => {
    if (!pageSize || !activePageSize) return;
    const el = tableRef.current;
    if (!el?.getTable) return;
    let cancelled = false;
    el.getTable().then((table) => {
      if (cancelled) return;
      table.setPageSize?.(activePageSize);
    });
    return () => {
      cancelled = true;
    };
  }, [activePageSize, pageSize, data]);

  useLayoutEffect(() => {
    const el = tableRef.current;
    if (!el) return;
    el.columns = tanstackColumns;
  }, [tanstackColumns]);

  useLayoutEffect(() => {
    const el = tableRef.current;
    if (!el) return;
    // Arrays and objects must be assigned as properties; attributes would stringify them.
    el.data = data as unknown as Record<string, unknown>[];
  }, [data]);

  useEffect(() => {
    if (!onSelectionChange) return;
    const el = tableRef.current;
    if (!el) return;

    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<{
        name?: string;
        selectedRows?: Array<{ original: T }>;
      }>).detail;
      if (detail?.name !== 'row-selection-changed') return;
      const rows = (detail.selectedRows ?? []).map((r) => r.original);
      lastSelectionRef.current = rows;
      onSelectionChange(rows);
    };

    el.addEventListener('prismChange', onChange);
    return () => el.removeEventListener('prismChange', onChange);
  }, [onSelectionChange]);

  // prism-table does not re-emit row-selection-changed when `data` shrinks, so a row that is
  // deleted while selected stays in the consumer's selection and can drive a bulk action against
  // a record that no longer exists. Prune against the live rows whenever the data changes.
  useEffect(() => {
    if (!onSelectionChange) return;
    const previous = lastSelectionRef.current;
    if (previous.length === 0) return;
    const live = new Map(data.map((row) => [rowIdOf(row), row]));
    const pruned = previous
      .map((row) => live.get(rowIdOf(row)))
      .filter((row): row is T => row !== undefined);
    if (pruned.length === previous.length) return;
    lastSelectionRef.current = pruned;
    onSelectionChange(pruned);
  }, [data, rowIdOf, onSelectionChange]);

  // prism-table owns the TanStack instance, so the filtered view is only reachable
  // through getTable(). Report it after data changes and after each filter change.
  useEffect(() => {
    if (!onFilteredRowsChange) return;
    const el = tableRef.current;
    if (!el?.getTable) return;

    let cancelled = false;
    const report = () => {
      el.getTable?.().then((table) => {
        if (cancelled) return;
        const rows = table.getFilteredRowModel().rows.map((r) => r.original);
        // Consumers often pass a freshly built `data` array each render and setState in this
        // callback; reporting identical rows again would re-render them forever.
        const previous = lastFilteredRef.current;
        if (previous && previous.length === rows.length && previous.every((row, i) => row === rows[i])) return;
        lastFilteredRef.current = rows;
        onFilteredRowsChange(rows);
      });
    };

    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<{ name?: string }>).detail;
      if (detail?.name === 'filter-changed' || detail?.name === 'sort-changed') report();
    };

    el.addEventListener('prismChange', onChange);
    // Let prism-table build its row model for the new data before reading it.
    const timer = window.setTimeout(report, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      el.removeEventListener('prismChange', onChange);
    };
  }, [data, onFilteredRowsChange]);

  // Row styles are reapplied after every render because prism-table rebuilds rows freely.
  // Previously-applied properties are tracked so a row that stops matching gets cleared.
  const styledRows = useRef<WeakMap<HTMLElement, string[]>>(new WeakMap());
  useEffect(() => {
    if (!rowStyle) return;
    hostsRef.current.forEach((entry) => {
      const tr = entry.node.closest('tr') as HTMLElement | null;
      if (!tr) return;
      const row = data.find((r) => rowIdOf(r) === entry.rowId);
      const previous = styledRows.current.get(tr) ?? [];
      previous.forEach((prop) => tr.style.removeProperty(prop));
      const style = row ? rowStyle(row) : undefined;
      if (!style) {
        styledRows.current.delete(tr);
        return;
      }
      const applied = Object.keys(style);
      applied.forEach((prop) => tr.style.setProperty(prop, style[prop]));
      styledRows.current.set(tr, applied);
    });
  });

  const commitEdit = useCallback(    async (col: DataGridColumn<T>, row: T, value: string) => {
      setEditing(null);
      const editor = col.editable;
      if (!editor) return;
      const current = col.accessor ? col.accessor(row) : readField(row, col.id);
      if (String(current ?? '') === value) return;
      if (editor.validate) {
        const verdict = editor.validate(value, row);
        if (verdict !== true) {
          onValidationError?.(verdict.message);
          return;
        }
      }
      await editor.onSave(row, value);
    },
    [onValidationError]
  );

  // Portals are rendered for every host whose row still exists. Deliberately NOT gated on
  // `node.isConnected`: prism-table detaches and re-attaches host nodes while re-rendering
  // rows, and skipping a detached host unmounts its portal, leaving the cell permanently
  // blank once the node is re-attached. Measured in design-lab/prism-data-grid.html.
  const portals: React.ReactNode[] = [];
  hostsRef.current.forEach((entry, key) => {
    const col = columns.find((c) => c.id === entry.colId);
    const row = data.find((r) => rowIdOf(r) === entry.rowId);
    if (!col || !row) return;

    const isEditing =
      editing?.rowId === entry.rowId && editing?.colId === entry.colId && !!col.editable;

    const content = isEditing ? (
      <GridCellEditor
        editor={col.editable as DataGridEditor<T>}
        initialValue={String((col.accessor ? col.accessor(row) : readField(row, col.id)) ?? '')}
        onCommit={(value) => commitEdit(col, row, value)}
        onCancel={() => setEditing(null)}
      />
    ) : col.cell ? (
      col.cell(row)
    ) : (
      <button
        type="button"
        className="prism-grid-edit-trigger"
        style={EDIT_TRIGGER_STYLE}
        onClick={() => setEditing({ rowId: entry.rowId, colId: entry.colId })}
      >
        {String((col.accessor ? col.accessor(row) : readField(row, col.id)) ?? '')}
      </button>
    );

    portals.push(
      <React.Fragment key={key}>{createPortal(content, entry.node)}</React.Fragment>
    );
  });

  return (
    <div className={className}>
      {pageSizeOptions && pageSizeOptions.length > 0 && pageSize ? (
        <div className="prism-grid-page-size" style={PAGE_SIZE_ROW_STYLE}>
          <label htmlFor={`${gridId}-page-size`}>Rows per page</label>
          <select
            id={`${gridId}-page-size`}
            style={PAGE_SIZE_SELECT_STYLE}
            value={activePageSize}
            onChange={(event) => setActivePageSize(Number(event.target.value))}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      {/* @ts-expect-error prism-table is a custom element */}
      <prism-table ref={tableRef} caption={caption} />
      {data.length === 0 && <p className="prism-grid-empty">{emptyMessage}</p>}
      {portals}
    </div>
  );
}

interface GridCellEditorProps<T> {
  editor: DataGridEditor<T>;
  initialValue: string;
  onCommit: (value: string) => void;
  onCancel: () => void;
}

function GridCellEditor<T>({
  editor,
  initialValue,
  onCommit,
  onCancel,
}: GridCellEditorProps<T>) {
  const [value, setValue] = useState(initialValue);
  const ref = useRef<HTMLInputElement | HTMLSelectElement>(null);

  useEffect(() => {
    ref.current?.focus();
    if (ref.current instanceof HTMLInputElement) ref.current.select();
  }, []);

  if (editor.type === 'select') {
    return (
      <select
        ref={ref as React.RefObject<HTMLSelectElement>}
        className="prism-grid-editor"
        style={EDITOR_STYLE}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          onCommit(e.target.value);
        }}
        onBlur={() => onCommit(value)}
      >
        {(editor.options ?? []).map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      ref={ref as React.RefObject<HTMLInputElement>}
      className="prism-grid-editor"
      style={EDITOR_STYLE}
      type={editor.type === 'number' ? 'number' : 'text'}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => onCommit(value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onCommit(value);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
    />
  );
}

export default PrismDataGrid;


